import { ref, type Ref } from 'vue';
import {
  listCurrentProcedures,
  listPhysicalLocations,
  type RecordConfigOption,
  type RecordConfigProcedureOption,
} from '../api/record-config-options.api';
import { recordOriginsApi } from '../api/record-origins.api';
import { recordStagesApi } from '../api/record-stages.api';

export interface RecordConfigOrigin {
  id: number;
  isGd: boolean;
}

export interface UseRecordConfigOptions {
  stages: Ref<RecordConfigOption[]>;
  procedures: Ref<RecordConfigProcedureOption[]>;
  physicalLocations: Ref<RecordConfigOption[]>;
  protectionMeans: Ref<RecordConfigOption[]>;
  isLoading: Ref<boolean>;
  load: () => Promise<void>;
  loadForOrigin: (origin: RecordConfigOrigin) => Promise<void>;
}

async function listStages(): Promise<RecordConfigOption[]> {
  const items = await recordStagesApi.list();

  return items.map(({ id, name }) => ({ id, name }));
}

async function listProtectionMeansOfOrigin(originId: number): Promise<RecordConfigOption[]> {
  const origins = await recordOriginsApi.list();
  const origin = origins.find(({ id }) => id === originId);

  return (origin?.protectionMeans ?? []).map(({ id, name }) => ({ id, name }));
}

/**
 * A failed list stays empty on purpose: each section shows its own empty state, and a toast here
 * would stack on the one `useRecordConfig` already raises when the form itself cannot load.
 */
export function useRecordConfigOptions(): UseRecordConfigOptions {
  const stages = ref<RecordConfigOption[]>([]);
  const procedures = ref<RecordConfigProcedureOption[]>([]);
  const physicalLocations = ref<RecordConfigOption[]>([]);
  const protectionMeans = ref<RecordConfigOption[]>([]);
  const isLoading = ref(false);
  let pendingRequests = 0;
  let loadPromise: Promise<void> | null = null;
  const originPromises = new Map<number, Promise<void>>();

  // `load` and `loadForOrigin` overlap, so the flag only drops when the last one settles.
  async function track(request: () => Promise<void>): Promise<void> {
    pendingRequests += 1;
    isLoading.value = true;
    try {
      await request();
    } finally {
      pendingRequests -= 1;
      isLoading.value = pendingRequests > 0;
    }
  }

  async function fetchAll(): Promise<void> {
    const [stageResult, procedureResult] = await Promise.allSettled([
      listStages(),
      listCurrentProcedures(),
    ]);
    if (stageResult.status === 'fulfilled') stages.value = stageResult.value;
    if (procedureResult.status === 'fulfilled') procedures.value = procedureResult.value;
  }

  async function fetchForOrigin({ id, isGd }: RecordConfigOrigin): Promise<void> {
    const [result] = await Promise.allSettled([
      isGd ? listPhysicalLocations() : listProtectionMeansOfOrigin(id),
    ]);
    if (result.status !== 'fulfilled') return;
    if (isGd) physicalLocations.value = result.value;
    else protectionMeans.value = result.value;
  }

  function load(): Promise<void> {
    loadPromise ??= track(fetchAll);

    return loadPromise;
  }

  function loadForOrigin(origin: RecordConfigOrigin): Promise<void> {
    let pending = originPromises.get(origin.id);
    if (!pending) {
      pending = track(() => fetchForOrigin(origin));
      originPromises.set(origin.id, pending);
    }

    return pending;
  }

  return { stages, procedures, physicalLocations, protectionMeans, isLoading, load, loadForOrigin };
}
